import React, { useEffect, useMemo, useState } from "react";
import { Button, Modal, Tooltip, message } from "antd";
import { DownloadOutlined, FilePdfOutlined, PrinterOutlined, QrcodeOutlined } from "@ant-design/icons";
import { saveAs } from "file-saver";
import { eventUseMutation } from "../../hooks";
import { Localize } from "components/service";
import { fileNamefromUrl } from "utils";

const ORIGIN_COORDINATE_THRESHOLD = 0.01;

const toNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
};

const hasMeaningfulCoordinates = (latitude, longitude) => {
    const lat = toNumber(latitude);
    const lng = toNumber(longitude);

    if (lat === null || lng === null)
    {
        return false;
    }

    return !(
        Math.abs(lat) < ORIGIN_COORDINATE_THRESHOLD &&
        Math.abs(lng) < ORIGIN_COORDINATE_THRESHOLD
    );
};

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
});

const getQrFileName = (url, eventID, extension = "png") => {
    try
    {
        return fileNamefromUrl(url) || `exhibition-${ eventID }-check-qr.${ extension }`;
    } catch (error)
    {
        return `exhibition-${ eventID }-check-qr.${ extension }`;
    }
};

const escapeHtml = (value) => String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const getQrCoordinatesKey = (event) => [
    toNumber(event?.check_latitude),
    toNumber(event?.check_longitude),
    toNumber(event?.check_radius),
].join(":");

const getQrStorageKey = (eventID) => `event-check-qr-coordinates-${ eventID }`;

const EventCheckQrGenerate = ({ event, eventID }) => {
    const [ modalOpen, setModalOpen ] = useState(false);
    const [ qrUrl, setQrUrl ] = useState(event?.getCheckQrCodeUrl);
    const [ pdfLoading, setPdfLoading ] = useState(false);

    const canGenerate = hasMeaningfulCoordinates(event?.check_latitude, event?.check_longitude);
    const currentCoordinatesKey = useMemo(() => getQrCoordinatesKey(event), [ event ]);
    const qrStorageKey = useMemo(() => getQrStorageKey(eventID), [ eventID ]);
    const hasQrUrl = !!qrUrl;
    const storedCoordinatesKey = window.localStorage.getItem(qrStorageKey);
    const needsQrRefresh = hasQrUrl && storedCoordinatesKey && storedCoordinatesKey !== currentCoordinatesKey;

    useEffect(() => {
        setQrUrl(event?.getCheckQrCodeUrl);
    }, [ event?.getCheckQrCodeUrl ]);

    const {
        _setEventCheckQrGenerate,
        loadingMutationEventCheckQrGenerate,
    } = eventUseMutation.checkQrGenerate({
        onCompleted: (payload) => {
            const nextQrUrl = payload?.exhibitionEvent?.getCheckQrCodeUrl;

            if (!nextQrUrl)
            {
                message.error(<Localize>CHECK_QR.Text_NoQrUrl</Localize>);
                return;
            }

            setQrUrl(nextQrUrl);
            window.localStorage.setItem(qrStorageKey, currentCoordinatesKey);
            setModalOpen(true);
        }
    });

    const openQr = () => {
        if (qrUrl && !needsQrRefresh)
        {
            if (!storedCoordinatesKey)
            {
                window.localStorage.setItem(qrStorageKey, currentCoordinatesKey);
            }

            setModalOpen(true);
            return;
        }

        _setEventCheckQrGenerate({ variables: { ee_id: eventID } });
    };

    const buttonLabel = hasQrUrl && !needsQrRefresh ?
        <Localize>CHECK_QR.Button_View</Localize> :
        needsQrRefresh ?
            <Localize>CHECK_QR.Button_Update</Localize> :
            <Localize>CHECK_QR.Button_Generate</Localize>;

    const downloadPng = () => {
        if (!qrUrl) return;
        saveAs(qrUrl, getQrFileName(qrUrl, eventID, "png"));
    };

    const downloadPdf = async () => {
        if (!qrUrl) return;

        setPdfLoading(true);

        try
        {
            const response = await fetch(qrUrl);
            const blob = await response.blob();
            const dataUrl = await blobToDataUrl(blob);
            const { jsPDF } = await import("jspdf");
            const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
            const imageType = blob.type?.includes("jpeg") || blob.type?.includes("jpg") ? "JPEG" : "PNG";

            doc.setFontSize(16);
            doc.text(event?.title || `Exhibition ${ eventID }`, 105, 25, { align: "center" });
            doc.addImage(dataUrl, imageType, 55, 40, 100, 100);

            saveAs(doc.output("blob"), `exhibition-${ eventID }-check-qr.pdf`);
        } catch (error)
        {
            message.error(<Localize>CHECK_QR.Text_PdfError</Localize>);
        } finally
        {
            setPdfLoading(false);
        }
    };

    const printQr = () => {
        if (!qrUrl) return;

        const printWindow = window.open("", "_blank", "width=640,height=720");
        const printTitle = escapeHtml(event?.title || `Exhibition ${ eventID }`);
        const printQrUrl = escapeHtml(qrUrl);

        if (!printWindow) return;

        printWindow.document.write(`
            <html>
                <head>
                    <title>QR Code</title>
                    <style>
                        body { font-family: Arial, sans-serif; text-align: center; padding: 40px; }
                        img { width: 320px; max-width: 100%; }
                    </style>
                </head>
                <body>
                    <h1>${ printTitle }</h1>
                    <img src="${ printQrUrl }" alt="QR Code" />
                </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        printWindow.onload = () => printWindow.print();
    };

    const button = (
        <Button
            className="ant-btn-secondary event-check-qr-generate__button"
            disabled={ !canGenerate }
            loading={ loadingMutationEventCheckQrGenerate }
            onClick={ openQr }>
            <QrcodeOutlined />
            <span>{ buttonLabel }</span>
        </Button>
    );

    return (
        <>
            { canGenerate ?
                button :
                <Tooltip title={ <Localize>CHECK_QR.Tooltip_FillCoordinates</Localize> }>
                    <span className="event-check-qr-generate__disabled">{ button }</span>
                </Tooltip>
            }

            <Modal
                className="modal-standard event-check-qr-modal"
                centered
                visible={ modalOpen }
                footer={ null }
                width={ 420 }
                destroyOnClose={ true }
                onCancel={ () => setModalOpen(false) }>
                <strong className="modal-title">
                    <Localize>CHECK_QR.Modal_Title</Localize>
                </strong>

                <div className="event-check-qr-modal__content">
                    { qrUrl &&
                        <img
                            className="event-check-qr-modal__image"
                            src={ qrUrl }
                            alt="QR Code" />
                    }

                    <div className="event-check-qr-modal__actions">
                        <Button
                            className="event-check-qr-modal__action-button event-check-qr-modal__action-button--primary"
                            onClick={ downloadPng }>
                            <DownloadOutlined /> <Localize>CHECK_QR.Button_DownloadPng</Localize>
                        </Button>
                        <Button
                            className="event-check-qr-modal__action-button"
                            onClick={ downloadPdf }
                            loading={ pdfLoading }>
                            <FilePdfOutlined /> <Localize>CHECK_QR.Button_DownloadPdf</Localize>
                        </Button>
                        <Button
                            className="event-check-qr-modal__action-button"
                            onClick={ printQr }>
                            <PrinterOutlined /> <Localize>CHECK_QR.Button_Print</Localize>
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default EventCheckQrGenerate;
